import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
	MAP_BASE_PATHS,
	MAP_GB_OUTLINE,
	MAP_REGION_CENTROIDS,
	MAP_REGION_PATHS,
	MAP_VIEWBOX,
} from '../models/finviseUkMap'
import { MONTHS, NAVY, NEUTRAL, PERI, monthStatusBuckets } from './finviseMetrics'

const FULL_MONTHS = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
]

const pct = (n, total) => (total ? Math.round((n / total) * 100) : 0)

const textLen = ref => (ref.current ? ref.current.getComputedTextLength() : 0)

/* ── KPI tile ───────────────────────────────────────────── */
export const KpiCard = ({ label, value, unit, sub, note, loading, disabled }) => (
	<div className={'card fvd-kpi' + (disabled ? ' under-development' : '')}>
		{loading && !disabled ? (
			<div className='card-loading'>
				<span className='spinner' />
			</div>
		) : null}
		<h3>
			{label}
			{disabled ? <span className='coming-soon'>Coming soon</span> : null}
		</h3>
		<div className='value'>
			{loading && !disabled ? (
				<div className='skeleton-value' />
			) : (
				<>
					{value}
					{unit ? <em>{unit}</em> : null}
				</>
			)}
		</div>
		<div className='sub'>{sub}</div>
		{note ? <div className='fvd-note'>{note}</div> : null}
	</div>
)

/* ── Case status: hover a bar segment or legend item ────── */
export const CaseStatusCard = ({ summary, loading }) => {
	const [hl, setHl] = useState(null)
	const hover = id => ({
		onMouseEnter: () => setHl(id),
		onMouseLeave: () => setHl(null),
	})
	return (
		<div className={'card fvd-status' + (hl ? ' active' : '')}>
			{loading ? (
				<div className='card-loading'>
					<span className='spinner' />
				</div>
			) : null}
			<h3>
				Case status
				<span className='big'>
					{summary.completed + ' of ' + summary.total}{' '}
					<span>completed</span>
				</span>
			</h3>
			<div
				className='sbar'
				role='img'
				aria-label={
					'Case status: ' +
					summary.items.map(i => i.n + ' ' + i.label.toLowerCase()).join(', ') +
					', of ' +
					summary.total +
					' instructions'
				}
			>
				{summary.items
					.filter(i => i.n > 0)
					.map(i => (
						<i
							key={i.id}
							className={hl === i.id ? 'hl' : undefined}
							style={{
								width: (i.n / summary.total) * 100 + '%',
								background: i.color,
							}}
							title={i.label + ': ' + i.n}
							{...hover(i.id)}
						/>
					))}
			</div>
			<div className='slegend'>
				{summary.items.map(i => (
					<div
						key={i.id}
						className={'si' + (hl === i.id ? ' hl' : '')}
						{...hover(i.id)}
					>
						<i style={{ background: i.color }} />
						<b>{i.n}</b> {i.label} <span>{i.pct + '%'}</span>
					</div>
				))}
			</div>
		</div>
	)
}

/* ── Cancelled instructions: inner ring = group, outer = reason ── */
const arcPath = (v0, v1, r0, r1, tot) => {
	const a0 = -Math.PI / 2 + (v0 / tot) * 2 * Math.PI + 0.012
	const a1 = -Math.PI / 2 + (v1 / tot) * 2 * Math.PI - 0.012
	const large = a1 - a0 > Math.PI ? 1 : 0
	const pt = (r, a) => r * Math.cos(a) + ' ' + r * Math.sin(a)
	return (
		'M' +
		pt(r1, a0) +
		' A' +
		r1 +
		' ' +
		r1 +
		' 0 ' +
		large +
		' 1 ' +
		pt(r1, a1) +
		' L' +
		pt(r0, a1) +
		' A' +
		r0 +
		' ' +
		r0 +
		' 0 ' +
		large +
		' 0 ' +
		pt(r0, a0) +
		'Z'
	)
}

export const CancelledCard = ({ data, periodText }) => {
	const [hk, setHk] = useState(null)
	const model = useMemo(() => {
		const groups = data.groups.map(g => ({
			...g,
			total: g.reasons.reduce((s, r) => s + r.cases, 0),
		}))
		const tot = groups.reduce((s, g) => s + g.total, 0)
		const info = {}
		const arcs = []
		let c = 0
		groups.forEach(g => {
			info[g.key] = { n: g.total, grp: g.key }
			if (g.total && tot)
				arcs.push({
					k: g.key,
					d: arcPath(c, c + g.total, 62, 90, tot),
					fill: g.color,
				})
			let sc = c
			g.reasons.forEach((r, i) => {
				const k = g.key + i
				info[k] = { n: r.cases, grp: g.key }
				if (r.cases && tot) {
					arcs.push({
						k,
						d: arcPath(sc, sc + r.cases, 95, 114, tot),
						fill: r.color,
					})
					sc += r.cases
				}
			})
			c += g.total
		})
		return { groups, tot, info, arcs }
	}, [data])
	const { groups, tot, info, arcs } = model

	const active = hk != null ? info[hk] : null
	const isGroup = active ? hk === active.grp : false
	const inSet = kk =>
		isGroup ? kk.indexOf(active.grp) === 0 : kk === hk || kk === active.grp
	const rowHl = kk => active && (isGroup ? kk.indexOf(active.grp) === 0 : kk === hk)

	const hover = k => ({
		onMouseEnter: () => setHk(k),
		onMouseLeave: () => setHk(null),
	})

	const centerNum = active ? (active.n == null ? '–' : active.n) : tot
	const centerLab = active ? (active.n ? pct(active.n, tot) + '%' : '') : 'cancelled'

	return (
		<div className={'card fvd-cancel' + (active ? ' active' : '')}>
			<h3>
				Cancelled instructions
				<small>{tot + ' ' + periodText + ', by reason'}</small>
			</h3>
			<div className='cwrap'>
				<div className='donut'>
					<svg
						viewBox='0 0 240 240'
						role='img'
						aria-label={
							tot +
							' cancelled instructions: ' +
							groups.map(g => g.total + ' ' + g.label.toLowerCase()).join(', ')
						}
					>
						<g transform='translate(120 120)'>
							{arcs.map(a => (
								<path
									key={a.k}
									d={a.d}
									fill={a.fill}
									className={
										active ? (inSet(a.k) ? 'on' : 'dim') : undefined
									}
									{...hover(a.k)}
								/>
							))}
						</g>
						<text
							x='120'
							y='118'
							textAnchor='middle'
							fontSize='44'
							fontWeight='700'
							fill='var(--ink)'
						>
							{centerNum}
						</text>
						<text
							x='120'
							y='140'
							textAnchor='middle'
							fontSize='12'
							fill='var(--muted)'
						>
							{centerLab}
						</text>
					</svg>
				</div>
				<div className='cgroups'>
					{groups.map(g => (
						<div className='cg' key={g.key}>
							<div
								className={'ch' + (rowHl(g.key) ? ' hl' : '')}
								{...hover(g.key)}
							>
								<i style={{ background: g.color }} />
								<b>{g.label}</b>
								<div className='n'>
									{g.total}
									<small>{pct(g.total, tot) + '%'}</small>
								</div>
							</div>
							{g.reasons.map((r, i) => {
								const k = g.key + i
								return (
									<div
										key={k}
										className={
											'cs' +
											(r.cases ? '' : ' zero') +
											(rowHl(k) ? ' hl' : '')
										}
										{...hover(k)}
									>
										<i style={{ background: r.color }} />
										<span>{r.label}</span>
										<b>{r.cases}</b>
										<em>{r.cases ? pct(r.cases, tot) + '%' : ''}</em>
									</div>
								)
							})}
						</div>
					))}
				</div>
			</div>
			<div className='fvd-footnote'>*Data available from 1 Nov 2026</div>
		</div>
	)
}

/* ── Instructions by month: stacked by status ───────────── */
const CH = { W: 560, H: 400, L: 44, R: 10, T: 20, B: 30 }

export const MonthsCard = ({ rows, from, to, loading, error, onRetry }) => {
	const { buckets, yMax, step } = useMemo(
		() => monthStatusBuckets(rows, from, to),
		[rows, from, to],
	)
	const [hi, setHi] = useState(null)
	const [tip, setTip] = useState(null)
	const lastHi = useRef(null)
	const t1 = useRef(null)
	const t2 = useRef(null)
	const t3 = useRef(null)
	if (hi != null) lastHi.current = hi

	const { W, H, L, R, T, B } = CH
	const iw = W - L - R
	const ih = H - T - B
	const slot = iw / Math.max(1, buckets.length)
	const bw = Math.min(34, slot * 0.58)
	const total = buckets.reduce((s, b) => s + b.total, 0)

	const ticks = []
	for (let v = 0; v <= yMax; v += step) ticks.push(v)

	const barGeom = i => {
		const b = buckets[i]
		return {
			x: L + slot * i + (slot - bw) / 2,
			y: T + ih - (b.total / yMax) * ih,
		}
	}

	// size the tooltip to its text once it has rendered
	useLayoutEffect(() => {
		if (hi == null || !buckets[hi]) return
		const tw = Math.max(textLen(t1), textLen(t2), textLen(t3)) + 20
		const th = 58
		const g = barGeom(hi)
		let tx = g.x + bw + 8
		if (tx + tw > W - R) tx = g.x - 8 - tw
		const ty = Math.max(T, Math.min(g.y - 4, T + ih - th))
		setTip({ x: tx, y: ty, w: tw, h: th })
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hi, buckets])

	// the tooltip keeps its last content while it fades out
	const hb = buckets[hi != null ? hi : lastHi.current] || null

	return (
		<div className={'card fvd-months' + (error ? ' has-error' : '')}>
			{loading ? (
				<div className='card-loading'>
					<span className='spinner' />
				</div>
			) : null}
			<h3>
				Instructions by month
				<small>
					{error ? 'Not available' : total + ' instructions, by status'}
				</small>
			</h3>
			<div className='mlegend'>
				<span>
					<i style={{ background: NAVY }} />
					Report completed
				</span>
				<span>
					<i style={{ background: PERI }} />
					In progress
				</span>
				<span>
					<i style={{ background: NEUTRAL }} />
					Cancelled
				</span>
			</div>
			{error ? (
				<div className='cerr' role='status'>
					<div className='ic'>
						<svg
							width='26'
							height='26'
							viewBox='0 0 24 24'
							fill='none'
							stroke='currentColor'
							strokeWidth='2'
							strokeLinecap='round'
							strokeLinejoin='round'
							aria-hidden='true'
						>
							<path d='M3 3v18h18' />
							<path d='M7 15l3-4 3 2 2-3' />
							<path d='M17 7l4 4M21 7l-4 4' />
						</svg>
					</div>
					<b>We couldn't load this chart</b>
					<p>
						Instructions by month is temporarily unavailable. The rest of your
						dashboard is up to date.
					</p>
					<button className='fvd-btn fvd-btn-primary' type='button' onClick={onRetry}>
						<RefreshIcon />
						Refresh
					</button>
				</div>
			) : (
				<div className='chart'>
					<svg
						viewBox={'0 0 ' + W + ' ' + H}
						role='img'
						aria-label='Instructions by month, split into report completed, in progress and cancelled'
					>
						{ticks.map(v => {
							const y = T + ih - (v / yMax) * ih
							return (
								<g key={v}>
									<line
										x1={L}
										x2={W - R}
										y1={y}
										y2={y}
										stroke={v === 0 ? 'var(--line)' : 'var(--grid)'}
										strokeWidth='1'
									/>
									<text
										x={L - 8}
										y={y + 4}
										textAnchor='end'
										fontSize='11'
										fill='var(--muted)'
									>
										{v}
									</text>
								</g>
							)
						})}
						{buckets.map((b, i) => {
							const { x, y } = barGeom(i)
							let yy = T + ih
							const cls =
								'bg' + (hi === i ? ' on' : hi != null ? ' dim' : '')
							return (
								<g
									key={i}
									className={cls}
									onMouseEnter={() => setHi(i)}
									onMouseLeave={() => setHi(null)}
								>
									<rect
										className='band'
										x={L + slot * i + 2}
										y={T}
										width={slot - 4}
										height={ih}
										rx='4'
										fill='var(--grid)'
									/>
									{[
										[b.done, NAVY],
										[b.prog, PERI],
										[b.canc, NEUTRAL],
									].map(([v, c], k) => {
										if (!v) return null
										const hh = (v / yMax) * ih
										yy -= hh
										return (
											<rect
												key={k}
												className='bar'
												x={x}
												y={yy}
												width={bw}
												height={hh}
												fill={c}
												stroke='var(--card)'
												strokeWidth='1'
											/>
										)
									})}
									<text
										x={x + bw / 2}
										y={y - 6}
										textAnchor='middle'
										fontSize='11'
										fontWeight='600'
										fill='var(--ink)'
									>
										{b.total}
									</text>
									<text
										className='mo'
										x={x + bw / 2}
										y={H - 10}
										textAnchor='middle'
										fontSize='11'
										fill='var(--muted)'
									>
										{MONTHS[b.m]}
									</text>
								</g>
							)
						})}
						<g className={'tip' + (hi != null && hb && tip ? ' show' : '')}>
							<rect
								rx='6'
								fill='var(--ink)'
								x={tip ? tip.x : 0}
								y={tip ? tip.y : 0}
								width={tip ? tip.w : 0}
								height={tip ? tip.h : 0}
							/>
							<text
								ref={t1}
								fontSize='11'
								fill='var(--card)'
								x={tip ? tip.x + 10 : 0}
								y={tip ? tip.y + 16 : 0}
							>
								{hb ? FULL_MONTHS[hb.m] + ' ' + hb.y : ''}
							</text>
							<text
								ref={t2}
								fontSize='13'
								fontWeight='700'
								fill='var(--card)'
								x={tip ? tip.x + 10 : 0}
								y={tip ? tip.y + 34 : 0}
							>
								{hb ? hb.total + ' instructions' : ''}
							</text>
							<text
								ref={t3}
								fontSize='11'
								fill='var(--card)'
								fillOpacity='0.75'
								x={tip ? tip.x + 10 : 0}
								y={tip ? tip.y + 50 : 0}
							>
								{hb
									? hb.done +
									  ' completed · ' +
									  (hb.prog ? hb.prog + ' in progress · ' : '') +
									  hb.canc +
									  ' cancelled (' +
									  pct(hb.canc, hb.total) +
									  '%)'
									: ''}
							</text>
						</g>
					</svg>
				</div>
			)}
		</div>
	)
}

const RefreshIcon = () => (
	<svg
		width='14'
		height='14'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='2.2'
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'
	>
		<path d='M21 12a9 9 0 1 1-2.64-6.36' />
		<path d='M21 3v6h-6' />
	</svg>
)

/* ── load-error toast ───────────────────────────────────── */
export const LoadErrorToast = ({ open, onRefresh, onDismiss }) => {
	// slide in a beat after the error so the page settles first
	const [shown, setShown] = useState(false)
	useLayoutEffect(() => {
		if (!open) {
			setShown(false)
			return
		}
		const t = setTimeout(() => setShown(true), 150)
		return () => clearTimeout(t)
	}, [open])
	if (!open) return null
	return (
		<div
			className={'fvd-toast' + (shown ? ' show' : '')}
			role='alert'
			aria-live='assertive'
		>
			<div className='ic'>
				<svg
					width='18'
					height='18'
					viewBox='0 0 24 24'
					fill='none'
					stroke='currentColor'
					strokeWidth='2.2'
					strokeLinecap='round'
					strokeLinejoin='round'
					aria-hidden='true'
				>
					<path d='M12 9v4M12 17h.01' />
					<path d='M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z' />
				</svg>
			</div>
			<div>
				<b>Some data didn't load</b>
				<span>
					We couldn't show instructions by month. Try refreshing the page.
				</span>
			</div>
			<button className='fvd-btn fvd-btn-primary' type='button' onClick={onRefresh}>
				<RefreshIcon />
				Refresh
			</button>
			<button
				className='fvd-btn fvd-btn-ghost'
				type='button'
				aria-label='Dismiss'
				onClick={onDismiss}
			>
				<svg
					width='16'
					height='16'
					viewBox='0 0 24 24'
					fill='none'
					stroke='currentColor'
					strokeWidth='2.2'
					strokeLinecap='round'
					aria-hidden='true'
				>
					<path d='M6 6l12 12M18 6 6 18' />
				</svg>
			</button>
		</div>
	)
}

/* ── Geography spread: map and list highlight each other ── */
// the long name is always set on two lines: on one line it fits until the
// bold hover style widens it, and the late wrap makes the list jump
const regionLabel = name =>
	/yorkshire and the humber/i.test(name) ? (
		<>
			Yorkshire &amp;
			<br />
			Humber
		</>
	) : (
		name
	)

// map viewBox, to position the HTML tooltip over the SVG in percentages
const VB = MAP_VIEWBOX.split(' ').map(Number)

const REGION_KEYS = Object.keys(MAP_REGION_PATHS).reduce((acc, name) => {
	acc[name.toLowerCase()] = name
	return acc
}, {})

export const GeographyCard = ({ regions, loading }) => {
	const [hr, setHr] = useState(null)
	const lastHr = useRef(null)
	if (hr != null) lastHr.current = hr

	const items = useMemo(
		() =>
			regions.map(r => ({
				...r,
				shape: REGION_KEYS[String(r.region).toLowerCase()] || null,
			})),
		[regions],
	)
	const total = items.reduce((s, r) => s + r.cases, 0)
	const maxN = Math.max(1, ...items.map(r => r.cases))

	const hovered = hr != null ? items[hr] : null
	const mapActive = hovered && hovered.shape
	// the tooltip keeps its last content while it fades out
	const shown = items[hr != null ? hr : lastHr.current] || null

	// anchor the tooltip at the region's centroid, on the side with more room
	const tipPos = shown && shown.shape ? MAP_REGION_CENTROIDS[shown.shape] : null
	const tipX = tipPos ? ((tipPos[0] - VB[0]) / VB[2]) * 100 : 0
	const tipY = tipPos ? ((tipPos[1] - VB[1]) / VB[3]) * 100 : 0

	const hover = i => ({
		onMouseEnter: () => setHr(i),
		onMouseLeave: () => setHr(null),
	})

	return (
		<div className='card fvd-geo-card'>
			{loading ? (
				<div className='card-loading'>
					<span className='spinner' />
				</div>
			) : null}
			<h3>
				Geography spread
				<small>{total + ' instructions by region'}</small>
			</h3>
			<div className='geo'>
				<div className='map'>
					<svg
						viewBox={MAP_VIEWBOX}
						role='img'
						aria-label='England and Wales regions shaded by number of instructions, darker means more'
					>
						{MAP_BASE_PATHS.map((d, i) => (
							<path
								key={i}
								d={d}
								fill='var(--land-far)'
								stroke='var(--coast)'
								strokeWidth='0.6'
								strokeLinejoin='round'
							/>
						))}
						{items.map((r, i) => {
							if (!r.shape) return null
							const col =
								'color-mix(in srgb, var(--navy) ' +
								Math.round(14 + (86 * r.cases) / maxN) +
								'%, var(--land))'
							return (
								<g
									key={r.region}
									className={
										'area' + (mapActive && hr !== i ? ' dim' : '')
									}
									{...hover(i)}
								>
									<path
										d={MAP_REGION_PATHS[r.shape]}
										strokeLinejoin='round'
										strokeWidth='0.7'
										style={{ fill: col, stroke: col }}
									/>
								</g>
							)
						})}
						<path
							d={MAP_GB_OUTLINE}
							fill='none'
							stroke='var(--coast)'
							strokeWidth='0.6'
							strokeLinejoin='round'
							pointerEvents='none'
						/>
					</svg>
					<div
						className={
							'map-tip' +
							(mapActive ? ' show' : '') +
							(tipX > 55 ? ' left' : '')
						}
						style={{ left: tipX + '%', top: tipY + '%' }}
						role='status'
					>
						<b>{shown && shown.shape ? shown.region : ''}</b>
						<span>
							{shown && shown.shape
								? shown.cases + ' instructions \u00b7 ' + pct(shown.cases, total) + '%'
								: ''}
						</span>
					</div>
				</div>
				<div className={'regions' + (hovered ? ' active' : '')}>
					{items.length ? (
						items.map((r, i) => (
							<div
								key={r.region}
								className={'reg' + (hr === i ? ' hl' : '')}
								{...hover(i)}
							>
								<span>{regionLabel(r.region)}</span>
								<div className='bar'>
									<b style={{ width: (r.cases / maxN) * 100 + '%' }} />
								</div>
								<span className='n'>{r.cases}</span>
							</div>
						))
					) : (
						<div className='fvd-empty'>No located properties in this period</div>
					)}
				</div>
				<div className='stats'>
					<div>
						Avg valuation<strong>{'\u2014'}</strong>
						<small>Coming soon</small>
					</div>
					<div>
						Total valued<strong>{'\u2014'}</strong>
						<small>Coming soon</small>
					</div>
				</div>
			</div>
		</div>
	)
}
